import { ProductAdminRequestDTO } from "Interface/Admin/Products/admin-product.interface";
import { ApiService } from "Services/ApiService";
import { ProductDetailDTO } from "Types/ProductDetailDTO";
import { ProductPackerDTO } from "Types/ProductPackerDTO";
import { ResultsDTO } from "Types/ResultsDTO";

export const productAdminApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAllAdminProducts: builder.query<
      ProductPackerDTO,
      {
        category: string | undefined;
        pageSize: number;
        activePage: number;
        subCategory: string | undefined;
        brand: string | undefined;
      }
    >({
      query: ({ category, pageSize, activePage, subCategory, brand }) => ({
        url: `/admin/products?mode=criteria&productsCount=${pageSize}&page=${activePage}`,
        method: "GET",
      }),
      providesTags: [{ type: "AdminProducts", id: "LIST" }],
      transformResponse: (res: ResultsDTO<ProductPackerDTO>) => res.results,
    }),

    getAdminProductById: builder.query<ProductDetailDTO, { id: number }>({
      query: ({ id }) => ({
        url: `/admin/products/getbyid/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "PrdProduct", id }],
      transformResponse: (res: ResultsDTO<ProductDetailDTO>) => res.results,
    }),

    createAdminProduct: builder.mutation<ResultsDTO, ProductAdminRequestDTO>({
      query: (product) => {
        const { isbnPrice, ...rest } = product;
        return {
          url: "/admin/products/create",
          method: "POST",
          headers: {
            origin: "origin - origin",
          },
          body: {
            ...rest,
            isbn: product.isbn || "",
            isbnPrice: isbnPrice != null ? Number(isbnPrice) : 0,
          },
        };
      },
      invalidatesTags: [{ type: "PrdProduct", id: "LIST" }],
    }),

    updateAdminProduct: builder.mutation<ResultsDTO, { id: number; data: ProductAdminRequestDTO }>({
      query: ({ id, data }) => {
        const { isbnPrice, ...rest } = data;
        return {
          url: `/admin/products/update/${id}`,
          method: "PUT",
          headers: {
            origin: "origin - origin",
          },
          body: {
            ...rest,
            isbn: data.isbn || "",
            isbnPrice: isbnPrice != null ? Number(isbnPrice) : 0,
          },
        };
      },
      invalidatesTags: (result, error, { id }) => [{ type: "PrdProduct", id }],
    }),

    deleteAdminProduct: builder.mutation<ResultsDTO, number>({
      query: (id) => ({
        url: `/admin/products/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "PrdProduct" }],
    }),

    uploadAdminProductMainImage: builder.mutation<ResultsDTO, { id: number; image: File }>({
      query: ({ id, image }) => {
        const formData = new FormData();
        formData.append("image", image);
        return {
          url: `/admin/products/upload-main-image/${id}`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { id }) => [{ type: "PrdProduct", id }],
    }),

    uploadAdminProductOtherImages: builder.mutation<
      ResultsDTO,
      { id: number; images: File[]; lnNo: number[] }
    >({
      query: ({ id, images, lnNo }) => {
        const formData = new FormData();
        images.forEach((file) => formData.append("images", file));
        lnNo.forEach((num) => {
          formData.append("lnNo", num.toString());
        });
        return {
          url: `/admin/products/upload-other-images/${id}`,
          method: "POST",
          body: formData,
          formData: true,
          credentials: "same-origin",
        };
      },
      invalidatesTags: (result, error, { id }) => [{ type: "PrdProduct", id }],
    }),

    deleteAdminProductMainImage: builder.mutation<ResultsDTO, number>({
      query: (id) => ({
        url: `/admin/products/delete-main-image/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "PrdProduct", id }],
    }),

    deleteAdminProductOtherImage: builder.mutation<ResultsDTO, { id: number; lnNo?: number }>({
      query: ({ id, lnNo }) => ({
        url: `/admin/products/delete-other-image/${id}`,
        method: "DELETE",
        params: lnNo !== undefined ? { lnNo } : undefined,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "PrdProduct", id }],
    }),

    generateProductCode: builder.query<string, void>({
      query: () => ({
        url: `/admin/products/generate-code`,
        method: "GET",
        headers: {
          origin: "origin - origin",
        },
      }),
      transformResponse: (res: ResultsDTO<string>) => res.results,
    }),

    deleteAdminProductsBulk: builder.mutation<ResultsDTO, number[]>({
      query: (ids) => ({
        url: `/admin/products/bulk-delete`,
        method: "DELETE",
        headers: {
          origin: "origin - origin",
        },
        params: { ids },
      }),
      invalidatesTags: () => [{ type: "PrdProduct", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllAdminProductsQuery,
  useGetAdminProductByIdQuery,
  useLazyGetAdminProductByIdQuery,
  useCreateAdminProductMutation,
  useUpdateAdminProductMutation,
  useDeleteAdminProductMutation,
  useUploadAdminProductMainImageMutation,
  useUploadAdminProductOtherImagesMutation,
  useDeleteAdminProductMainImageMutation,
  useDeleteAdminProductOtherImageMutation,
  useGenerateProductCodeQuery,
  useLazyGenerateProductCodeQuery,
  useDeleteAdminProductsBulkMutation,
} = productAdminApiSlice;
